import { SpeedInsights } from '@vercel/speed-insights/react';
import { useEffect } from 'react';
import type { AppProps } from 'next/app';
import { DevTools } from 'jotai-devtools';
import { Provider } from 'jotai';
import Cookies from 'js-cookie';
import Router, { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';

// --> imports
import { AuthGuard, consumeLoginTokenFromUrl, rememberArrivalDestination } from '@restworld/feature-authentication';
import { Spinner } from '@restworld/ui-ds-base';
// --> components and wrappers
import { MainContextWrapper } from '../context';
import { ErrorBoundaryWrapper } from '../components';
import { ProgressBar } from '../components/utility-components/progress-bar';
import { CanonicalUrl, initOpenObserveRum } from '@restworld/utils-common';
// --> css
import 'maplibre-gl/dist/maplibre-gl.css';
import 'react-alice-carousel/lib/alice-carousel.css';
import 'rc-slider/assets/index.css';
import GlobalStyles from '../styles/GlobalStyles';
import '../styles/globals.css';
import '@restworld/utils-common/iubenda-banner.css';
import nextI18NextConfig from '../next-i18next.config';

// Picks up what the bootstrap script in `_document` left, as early as the bundle can run.
// The script is the one that beats Next to the URL; this call is the fallback for the case
// where it did not run at all, and it is what puts the value where the login screen and the
// auth guard read it.
consumeLoginTokenFromUrl();

// And the other half of the link, for the same reason and at the same moment. The destination
// lives in `?redirect_url=`, which several things rewrite during a login — the bootstrap
// script, Next's own hydration replace, and the login screen tidying the spent token away.
// Read once here, before any of them, so that what the worker asked for cannot be lost by
// something later deciding the URL should look different. See `login-destination.ts`.
rememberArrivalDestination();

const CustomApp = ({ Component, pageProps }: AppProps) => {
  const { locale, asPath, pathname } = useRouter();
  useEffect(() => {
    if (Cookies.get('NEXT_LOCALE') && locale !== Cookies.get('NEXT_LOCALE'))
      Router.push(pathname, asPath, { locale: Cookies.get('NEXT_LOCALE') });
    // No-op unless NEXT_PUBLIC_OPENOBSERVE_RUM_CLIENT_TOKEN is set (worker-app's RUM app).
    initOpenObserveRum({ service: 'restworld-worker-app' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Provider>
        <SpeedInsights route={pathname} />
        <CanonicalUrl />
        <GlobalStyles />
        <ErrorBoundaryWrapper>
          <MainContextWrapper>
            {/* Outside the guard on purpose: `AuthGuard` swaps its children for a loader during
                a login hand-over, and unmounting the bar mid-navigation strands it. */}
            <ProgressBar />
            <AuthGuard loader={<Spinner size="lg" color="primary" />}>
              <Component {...pageProps} />
              <DevTools />
            </AuthGuard>
          </MainContextWrapper>
        </ErrorBoundaryWrapper>
      </Provider>
    </>
  );
};
export default appWithTranslation(CustomApp, nextI18NextConfig);
